// @ts-nocheck

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { query, orderBy, limit, onSnapshot, collectionGroup, doc, updateDoc, collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import {
  ScraperStatusCard,
  TaskQueue,
  VisitedProfiles,
  ShopKeyPromptModal,
  PostGalleryModal,
  ShopSearchDrawer,
  OnboardQueue,
  QuickCollections,
  ScraperViewport,
  AutoModeControl,
  ManualControl,
  OfflineBanner,
  IGSyncTask,
  ScraperStatus,
  ShopKeyPrompt,
  TransformedPost,
  SAVED_COLLECTIONS
} from '../components/scraper';

const IGScraperControl: React.FC = () => {
  const [tasks, setTasks] = useState<IGSyncTask[]>([]);
  const [scraperStatus, setScraperStatus] = useState<ScraperStatus | null>(null);
  const [isScraperConnected, setIsScraperConnected] = useState(false);
  const [manualShopId, setManualShopId] = useState('');
  const [shopKeyPrompt, setShopKeyPrompt] = useState<ShopKeyPrompt | null>(null);

  // Auto mode state
  const [autoModeEnabled, setAutoModeEnabled] = useState(false);
  const [includeSavedPosts, setIncludeSavedPosts] = useState(false);
  const [minDelay, setMinDelay] = useState(5);
  const [maxDelay, setMaxDelay] = useState(15);

  // Post gallery modal state
  const [galleryUsername, setGalleryUsername] = useState('');
  const [galleryPosts, setGalleryPosts] = useState<TransformedPost[]>([]);
  const [isGalleryOpen, setIsGalleryOpen] = useState(false);

  // Shop search drawer state
  const [isShopDrawerOpen, setIsShopDrawerOpen] = useState(false);

  // Onboard queue: track which record is currently being fetched
  const activeOnboardRef = useRef<{ id: string; username: string } | null>(null);

  // Listen for pending tasks
  useEffect(() => {
    const twentyFourHoursAgo = new Date();
    twentyFourHoursAgo.setHours(twentyFourHoursAgo.getHours() - (24 * 7));

    const unsubscribe = onSnapshot(
      query(
        collectionGroup(db, 'igSyncTasks'),
        orderBy('createdAt', 'desc'),
        limit(50)
      ),
      (snapshot) => {
        const taskList = snapshot.docs
          .map(doc => {
            const data = doc.data();
            const shopId = doc.ref.parent.parent?.id || '';
            return { id: doc.id, path: doc.ref.path, shopId, ...data } as IGSyncTask;
          })
          .filter(task => {
            const createdAt = task.createdAt?.toDate?.() || new Date(0);
            return createdAt > twentyFourHoursAgo &&
              (task.status === 'pending' || task.status === 'queued' || task.status === 'in_progress');
          });
        setTasks(taskList);
      }
    );
    return () => unsubscribe();
  }, []);

  // Poll scraper status and shop key prompt
  useEffect(() => {
    const interval = setInterval(() => {
      const commands = (window as any).__scraperCommands;
      if (commands) {
        setScraperStatus({ ...commands });
        setIsScraperConnected(true);
        if (commands.autoMode) {
          setAutoModeEnabled(commands.autoMode.enabled);
          setIncludeSavedPosts(commands.autoMode.includeSavedPosts);
          setMinDelay(Math.floor(commands.autoMode.minDelay / 1000));
          setMaxDelay(Math.floor(commands.autoMode.maxDelay / 1000));
        }
      } else {
        setIsScraperConnected(false);
      }
      const prompt = (window as any).__shopKeyPrompt;
      if (prompt) setShopKeyPrompt({ ...prompt });
    }, 500);
    return () => clearInterval(interval);
  }, []);

  const hasCurrentCommand = !!scraperStatus?.currentCommand;

  // Scraper command helpers
  const sendCommand = (command: any) => {
    const commands = (window as any).__scraperCommands;
    if (!commands || commands.currentCommand) return;
    commands.currentCommand = command;
  };

  const handleProcessTask = (task: IGSyncTask) => {
    sendCommand({
      action: 'process',
      task: { id: task.id, path: task.path, shopId: task.shopId, instagramUrl: task.instagramUrl, resultsLimit: task.resultsLimit }
    });
  };

  const handleSkipTask = (task: IGSyncTask) => {
    sendCommand({
      action: 'skip',
      task: { id: task.id, path: task.path, shopId: task.shopId, instagramUrl: task.instagramUrl }
    });
  };

  const handleFinishImport = () => {
    const commands = (window as any).__scraperCommands;
    if (commands) commands.currentSignal = 'finish';
  };

  const handleImportProfile = (username: string) => {
    sendCommand({ action: 'manual-import', manualImport: { username, shopId: undefined } });
  };

  const handleClearProfile = (username: string) => {
    const commands = (window as any).__scraperCommands;
    if (commands?.visitedProfiles) delete commands.visitedProfiles[username];
  };

  const handleViewPosts = (username: string) => {
    const commands = (window as any).__scraperCommands;
    const posts = commands?.visitedProfiles?.[username]?.posts || [];
    setGalleryUsername(username);
    setGalleryPosts(posts);
    setIsGalleryOpen(true);
  };

  const handleImportSelected = (username: string, selectedPosts: TransformedPost[]) => {
    sendCommand({
      action: 'manual-import',
      manualImport: { username, shopId: undefined, selectedPosts }
    });
  };

  const handleQuickImport = () => {
    sendCommand({
      action: 'manual-import',
      manualImport: { username: scraperStatus?.currentUsername || 'manual', shopId: undefined }
    });
  };

  const handleManualImport = () => {
    if (!manualShopId) return;
    sendCommand({
      action: 'manual-import',
      manualImport: { username: scraperStatus?.currentUsername || 'manual', shopId: manualShopId }
    });
  };

  const handleToggleAutoMode = () => {
    sendCommand({
      action: 'auto-mode',
      autoMode: { enabled: !autoModeEnabled, includeSavedPosts, minDelay: minDelay * 1000, maxDelay: maxDelay * 1000 }
    });
  };

  const handleUpdateAutoModeSettings = () => {
    sendCommand({
      action: 'auto-mode',
      autoMode: { enabled: autoModeEnabled, includeSavedPosts, minDelay: minDelay * 1000, maxDelay: maxDelay * 1000 }
    });
  };

  const handleNavigateToCollection = (collection: typeof SAVED_COLLECTIONS[0]) => {
    sendCommand({ action: 'navigate', navigate: { url: collection.url, shopId: collection.shopId } });
  };

  const handleSelectShop = (shop: { id: string; instagramUrl: string; username: string }) => {
    sendCommand({ action: 'navigate', navigate: { url: shop.instagramUrl, shopId: shop.id } });
    setIsShopDrawerOpen(false);
  };

  const handleAddShopsToQueue = async (shops: { id: string; instagramUrl: string; username: string }[]) => {
    for (const shop of shops) {
      try {
        await addDoc(collection(db, 'shops', shop.id, 'igSyncTasks'), {
          instagramUrl: shop.instagramUrl,
          status: 'pending',
          createdAt: serverTimestamp(),
          source: 'manual-batch'
        });
      } catch (error) {
        console.error(`Failed to create task for shop ${shop.id}:`, error);
      }
    }
    setIsShopDrawerOpen(false);
  };

  // ── Onboard queue auto-fetch ──────────────────────────────────────────
  const handleProcessOnboard = useCallback(async (record: any) => {
    if (!record?.username) return;
    // Mark as fetching in Firestore
    try {
      await updateDoc(doc(db, 'onboardQueue', record.id), { status: 'fetching' });
    } catch (e) {
      console.error('Failed to update onboard status:', e);
    }
    // Track which record we're fetching for
    activeOnboardRef.current = { id: record.id, username: record.username };
    // Use onboard-fetch: navigates to profile, auto-scrolls, stores in visitedProfiles — no shop needed
    sendCommand({
      action: 'onboard-fetch',
      onboardFetch: { username: record.username }
    });
  }, []);

  // Watch visited profiles — poll directly from window.__scraperCommands for reliability
  // This avoids React effect timing issues with shallow state copies
  const onboardWritingRef = useRef(false);
  useEffect(() => {
    const check = setInterval(async () => {
      if (!activeOnboardRef.current || onboardWritingRef.current) return;
      const { id, username } = activeOnboardRef.current;

      const commands = (window as any).__scraperCommands;
      const profile = commands?.visitedProfiles?.[username];
      if (!profile || !profile.posts || profile.posts.length === 0) return;

      // Posts are ready — write them back to the onboard record
      onboardWritingRef.current = true;
      const posts = profile.posts;
      console.log(`[Onboard] Detected ${posts.length} posts for @${username}, writing to Firestore...`);

      try {
        await updateDoc(doc(db, 'onboardQueue', id), {
          status: 'ready',
          posts,
          postCount: posts.length,
        });
        console.log(`[Onboard] ✓ Wrote ${posts.length} posts for @${username}`);
        activeOnboardRef.current = null;
      } catch (e) {
        console.error('[Onboard] Failed to write posts:', e);
        try {
          await updateDoc(doc(db, 'onboardQueue', id), {
            status: 'error',
            error: 'Failed to write posts back',
          });
        } catch (_) { }
        activeOnboardRef.current = null;
      } finally {
        onboardWritingRef.current = false;
      }
    }, 1000);
    return () => clearInterval(check);
  }, []);

  return (
    <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Instagram Scraper</h1>
            <p className="mt-2 text-gray-600">Central control for syncing Instagram posts</p>
          </div>
          <div className="flex items-center space-x-3">
            <button
              onClick={() => setIsShopDrawerOpen(true)}
              className="flex items-center space-x-2 px-4 py-2 rounded-full bg-purple-50 border border-purple-200 hover:bg-purple-100 transition-colors"
            >
              <svg className="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <span className="text-sm font-medium text-purple-700">Shops</span>
            </button>
            <div className={`flex items-center space-x-2 px-4 py-2 rounded-full ${isScraperConnected ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
              <div className={`w-2 h-2 rounded-full ${isScraperConnected ? 'bg-green-500' : 'bg-red-500'}`} />
              <span className={`text-sm font-medium ${isScraperConnected ? 'text-green-700' : 'text-red-700'}`}>
                {isScraperConnected ? 'Scraper Live' : 'Scraper Offline'}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Left Column */}
        <div className="lg:col-span-2 space-y-6">
          {scraperStatus && (
            <ScraperStatusCard scraperStatus={scraperStatus} onFinishImport={handleFinishImport} />
          )}

          {scraperStatus?.visitedProfiles && (
            <VisitedProfiles
              visitedProfiles={scraperStatus.visitedProfiles}
              isScraperConnected={isScraperConnected}
              hasCurrentCommand={hasCurrentCommand}
              onImportProfile={handleImportProfile}
              onViewPosts={handleViewPosts}
              onClearProfile={handleClearProfile}
            />
          )}

          <OnboardQueue
            isScraperConnected={isScraperConnected}
            hasCurrentCommand={hasCurrentCommand}
            onProcessOnboard={handleProcessOnboard}
          />

          <TaskQueue
            tasks={tasks}
            isScraperConnected={isScraperConnected}
            hasCurrentCommand={hasCurrentCommand}
            onProcessTask={handleProcessTask}
            onSkipTask={handleSkipTask}
          />
        </div>

        {/* Right Column */}
        <div className="space-y-6">
          <QuickCollections
            isScraperConnected={isScraperConnected}
            hasCurrentCommand={hasCurrentCommand}
            onNavigate={handleNavigateToCollection}
          />

          <ScraperViewport scraperStatus={scraperStatus} />

          <AutoModeControl
            autoModeEnabled={autoModeEnabled}
            includeSavedPosts={includeSavedPosts}
            minDelay={minDelay}
            maxDelay={maxDelay}
            isScraperConnected={isScraperConnected}
            hasCurrentCommand={hasCurrentCommand}
            onToggleAutoMode={handleToggleAutoMode}
            onSetIncludeSavedPosts={setIncludeSavedPosts}
            onSetMinDelay={setMinDelay}
            onSetMaxDelay={setMaxDelay}
            onUpdateSettings={handleUpdateAutoModeSettings}
          />

          <ManualControl
            isScraperConnected={isScraperConnected}
            hasCurrentCommand={hasCurrentCommand}
            currentUsername={scraperStatus?.currentUsername}
            manualShopId={manualShopId}
            onSetManualShopId={setManualShopId}
            onQuickImport={handleQuickImport}
            onManualImport={handleManualImport}
          />

          {!isScraperConnected && <OfflineBanner />}
        </div>
      </div>

      {/* Modals */}
      <ShopKeyPromptModal shopKeyPrompt={shopKeyPrompt} />

      <PostGalleryModal
        isOpen={isGalleryOpen}
        username={galleryUsername}
        posts={galleryPosts}
        onClose={() => setIsGalleryOpen(false)}
        onImportSelected={handleImportSelected}
      />

      <ShopSearchDrawer
        isOpen={isShopDrawerOpen}
        onClose={() => setIsShopDrawerOpen(false)}
        onSelectShop={handleSelectShop}
        onAddToQueue={handleAddShopsToQueue}
      />
    </div>
  );
};

export default IGScraperControl;
